import { useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../common/Modal';
import {
  submitEntityRequest,
  type CalibreRequestPayload,
  type EntityRequestContext,
  type EntityRequestType,
  type LookupRequestPayload,
} from '../../services/firebase';

/**
 * RequestNewValueModal — Brand Portal Phase 2.5 (chunk ii).
 *
 * The submission half of the entity-request flow. Brand owners hit an
 * `allowCustom={false}` picker (calibre / watch type / occasion / parent org /
 * …), can't find the value they need, and open this modal to ask an admin to
 * add it. One modal serves both request types via the `type` discriminator:
 *   • 'calibre' — name (required) + manufacturer / movement type / notes.
 *   • 'lookup'  — a single proposed value (required) + notes, scoped to the
 *     picker's `collection` (shown humanized in the header).
 *
 * On submit it calls the `submitEntityRequest` callable; the request lands in
 * Firestore as `status: 'pending'`. The owner can't see status yet (that's
 * chunk iii) — success is just a "thanks, we'll review it" toast. Form state is
 * preserved on failure so an unexpected error doesn't lose the owner's typing.
 *
 * The caller owns visibility (`visible`) and renders this alongside the picker
 * that raised it; `context` carries where the request came from so the admin
 * sees the situation.
 */

interface Props {
  visible: boolean;
  brandId: string;
  type: EntityRequestType;
  /** Required when `type === 'lookup'` (e.g. 'lookup_occasions'). */
  collection?: string;
  /** Optional seed for the primary value (e.g. whatever the owner typed). */
  initialValue?: string;
  /** What edit context the request was raised from. */
  context?: EntityRequestContext;
  onClose: () => void;
  onSubmitted?: (requestId: string) => void;
}

const NAME_MAX = 100;
const NOTES_MAX = 500;

/** Known lookup slug → friendly label; unknowns fall back to a title-cased slug. */
const LOOKUP_DISPLAY_NAMES: Record<string, string> = {
  lookup_occasions: 'Occasion',
  lookup_watch_occasion: 'Occasion',
  lookup_watch_types: 'Watch type',
  lookup_case_materials: 'Case material',
  lookup_dial_colors: 'Dial color',
  lookup_movement_types: 'Movement type',
  lookup_strap_types: 'Strap type',
  lookup_bezel_materials: 'Bezel material',
  lookup_hand_types: 'Hand type',
  lookup_parent_organization: 'Parent organisation',
  lookup_winding_directions: 'Winding direction',
};

/** lookup_occasions → "Occasion"; unknown → title-cased de-slugged fallback. */
export function humanizeLookupCollection(slug: string): string {
  if (LOOKUP_DISPLAY_NAMES[slug]) return LOOKUP_DISPLAY_NAMES[slug];
  const stripped = slug.replace(/^lookup_/, '').replace(/_/g, ' ').trim();
  if (!stripped) return slug;
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

const inputClass =
  'w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

/** Firebase callable error codes arrive as either 'x' or 'functions/x'. */
function codeIs(err: unknown, suffix: string): boolean {
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' && code.endsWith(suffix);
}

export function RequestNewValueModal({
  visible,
  brandId,
  type,
  collection,
  initialValue = '',
  context,
  onClose,
  onSubmitted,
}: Props) {
  // Calibre fields
  const [name, setName] = useState(initialValue);
  const [manufacturer, setManufacturer] = useState('');
  const [movementType, setMovementType] = useState('');
  // Lookup field
  const [value, setValue] = useState(initialValue);
  // Shared
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const primary = type === 'calibre' ? name : value;
  const isDirty =
    primary.trim() !== initialValue.trim() ||
    manufacturer.trim() !== '' ||
    movementType.trim() !== '' ||
    notes.trim() !== '';
  const canSubmit = primary.trim() !== '' && !submitting;

  const resetAndClose = () => {
    // Clear local state so a re-open (possibly with a different seed) starts clean.
    setName(initialValue);
    setManufacturer('');
    setMovementType('');
    setValue(initialValue);
    setNotes('');
    setError(null);
    onClose();
  };

  const handleCancel = () => {
    if (isDirty && !window.confirm('Discard request?')) return;
    resetAndClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    if (type === 'lookup' && !collection) {
      // Defensive — the caller must pass a collection for lookup requests.
      setError('Missing target collection for this request.');
      return;
    }

    const payload: CalibreRequestPayload | LookupRequestPayload =
      type === 'calibre'
        ? {
            name: name.trim(),
            manufacturer: manufacturer.trim() || undefined,
            movementType: movementType.trim() || undefined,
            notes: notes.trim() || undefined,
          }
        : {
            collection: collection as string,
            value: value.trim(),
            notes: notes.trim() || undefined,
          };

    setSubmitting(true);
    setError(null);
    try {
      const { requestId } = await submitEntityRequest({
        brandId,
        type,
        payload,
        context,
      });
      toast.success(
        "Request submitted — we'll notify you when an admin reviews it",
      );
      onSubmitted?.(requestId);
      resetAndClose();
    } catch (err) {
      if (codeIs(err, 'resource-exhausted')) {
        toast.error(
          'You have too many open requests for this brand. Wait for some to be resolved before submitting more.',
        );
      } else if (codeIs(err, 'invalid-argument')) {
        const message =
          err instanceof Error ? err.message : 'The request was rejected.';
        // Surface the server's reason inline; keep form state for a retry.
        setError(message);
      } else {
        const message =
          err instanceof Error ? err.message : 'Failed to submit request';
        toast.error(message);
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const title =
    type === 'calibre' ? 'Request a new calibre' : 'Request a new value';

  return (
    <Modal isOpen={visible} onClose={handleCancel} title={title} size='md'>
      <form onSubmit={handleSubmit} className='space-y-4'>
        {type === 'lookup' && collection && (
          <p className='text-sm text-gray-500'>
            Requesting a new value for:{' '}
            <span className='font-medium text-gray-700'>
              {humanizeLookupCollection(collection)}
            </span>
          </p>
        )}
        {type === 'calibre' && (
          <p className='text-sm text-gray-500'>
            Tell us about the calibre you'd like added. An admin will review and
            add it to the catalogue.
          </p>
        )}

        {type === 'calibre' ? (
          <>
            <div>
              <label className={labelClass}>
                Name<span className='text-red-500'> *</span>
              </label>
              <input
                type='text'
                value={name}
                maxLength={NAME_MAX}
                onChange={(e) => setName(e.target.value)}
                placeholder='e.g. In-house Calibre 4130'
                className={inputClass}
                autoFocus
              />
            </div>
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <div>
                <label className={labelClass}>Manufacturer</label>
                <input
                  type='text'
                  value={manufacturer}
                  maxLength={NAME_MAX}
                  onChange={(e) => setManufacturer(e.target.value)}
                  placeholder='Who makes it'
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Movement type</label>
                <input
                  type='text'
                  value={movementType}
                  maxLength={NAME_MAX}
                  onChange={(e) => setMovementType(e.target.value)}
                  placeholder='e.g. automatic, manual, quartz'
                  className={inputClass}
                />
              </div>
            </div>
          </>
        ) : (
          <div>
            <label className={labelClass}>
              Value<span className='text-red-500'> *</span>
            </label>
            <input
              type='text'
              value={value}
              maxLength={NAME_MAX}
              onChange={(e) => setValue(e.target.value)}
              placeholder='The value you’d like added'
              className={inputClass}
              autoFocus
            />
          </div>
        )}

        <div>
          <label className={labelClass}>Notes</label>
          <textarea
            value={notes}
            maxLength={NOTES_MAX}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder='Anything the admin should know (specs, context, justification)…'
            className={inputClass}
          />
          {notes.length > 400 && (
            <p className='mt-1 text-right text-xs text-gray-400'>
              {notes.length}/{NOTES_MAX}
            </p>
          )}
        </div>

        {error && (
          <div className='rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700'>
            {error}
          </div>
        )}

        <div className='flex items-center justify-end gap-3 pt-2 border-t border-gray-100'>
          <button
            type='button'
            onClick={handleCancel}
            disabled={submitting}
            className='px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50'
          >
            Cancel
          </button>
          <button
            type='submit'
            disabled={!canSubmit}
            className='px-4 py-2 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50'
          >
            {submitting ? 'Submitting…' : 'Submit request'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
